import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Loader2 } from "lucide-react";
import { formatApiError } from "@/lib/formatApiError";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Le mot de passe doit contenir au moins 6 caractères");
    if (password !== confirm) return setError("Les deux mots de passe ne correspondent pas");
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      toast.success("Mot de passe mis à jour. Vous pouvez vous connecter.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Erreur lors de la réinitialisation"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <Card className="w-full max-w-md" data-testid="reset-password-card">
        <CardHeader className="space-y-2 text-center">
          <img src="/logo-bati-assistant.svg" alt="Bati Assistant" className="mx-auto h-16 w-16 mb-2" />
          <CardTitle className="font-heading text-2xl">Nouveau mot de passe</CardTitle>
          <CardDescription>Choisissez un nouveau mot de passe (6 caractères minimum).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!token ? (
            <p className="text-sm text-destructive" data-testid="reset-password-missing-token">
              Lien incomplet. Refaites une demande depuis « Mot de passe oublié ».
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="reset-password">Nouveau mot de passe</Label>
                <Input id="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  required className="h-11" data-testid="reset-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reset-password-confirm">Confirmer le mot de passe</Label>
                <Input id="reset-password-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  required className="h-11" data-testid="reset-password-confirm-input" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="reset-password-error">{error}</p>}
              <Button type="submit" className="w-full h-11" disabled={loading} data-testid="reset-password-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                Enregistrer
              </Button>
            </form>
          )}
          <Link to="/forgot-password" className="flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            data-testid="reset-password-new-request-link">
            <ArrowLeft className="h-4 w-4" /> Faire une nouvelle demande
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
